import { useState, useEffect, useCallback, useRef, type RefObject } from 'react'

/**
 * usePiP manages Picture-in-Picture for the call system.
 *
 * Uses the Document Picture-in-Picture API (Chrome 116+) which allows
 * putting arbitrary HTML (video tiles + controls) in a floating window.
 * Falls back to classic PiP (single <video> element) if unavailable.
 *
 * The PiP window is stored in a module-level singleton so it survives
 * CallView remounts (e.g. navigating between the call page and another
 * page swaps the embedded CallView for the hidden GlobalCallOverlay
 * instance). Without the singleton, each remount would orphan the old
 * PiP window and open a new one (or fail without user activation).
 *
 * Usage:
 *   const { pipActive, pipSupported, pipContainer, openPiP, closePiP } = usePiP({
 *     localVideoRef,  // ref to the local <video> element (for classic fallback)
 *     isActive,       // whether the call is active (callState !== 'idle')
 *   })
 */

// Module-level singleton: persists across CallView remounts.
const _sharedPip = { window: null as Window | null, container: null as HTMLDivElement | null }

export function usePiP({ localVideoRef, isActive }: { localVideoRef: RefObject<HTMLVideoElement | null>; isActive: boolean }) {
  const [pipActive, setPipActive] = useState(false)
  const [pipSupported, setPipSupported] = useState<boolean | string>(false)
  const [pipContainer, setPipContainer] = useState<HTMLDivElement | null>(null)
  const pipWindowRef = useRef<Window | null>(null)

  // Detect support on mount.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if ('documentPictureInPicture' in window) {
        setPipSupported('document')
      } else if (document.pictureInPictureEnabled) {
        setPipSupported('classic')
      }
    }
  }, [])

  // On mount, reuse an existing PiP window left over from a previous
  // CallView instance (e.g. after navigating between pages).
  useEffect(() => {
    if (_sharedPip.window && !_sharedPip.window.closed && _sharedPip.container) {
      pipWindowRef.current = _sharedPip.window
      setPipContainer(_sharedPip.container)
      setPipActive(true)
    }
  }, [])

  // Keep state in sync when the PiP window is closed by a listener
  // registered on a different (now unmounted) CallView instance.
  useEffect(() => {
    const onClosed = () => {
      pipWindowRef.current = null
      setPipContainer(null)
      setPipActive(false)
    }
    window.addEventListener('atlas-pip-closed', onClosed)
    return () => window.removeEventListener('atlas-pip-closed', onClosed)
  }, [])

  const openClassicPiP = useCallback(async () => {
    const video = localVideoRef?.current
    if (video && video.srcObject) {
      try {
        await video.requestPictureInPicture()
        setPipActive(true)
      } catch (err: unknown) {
        if ((err as Error).name !== 'NotAllowedError') {
          console.error('PiP: classic PiP failed:', err)
        }
      }
    }
  }, [localVideoRef])

  const openPiP = useCallback(async () => {
    // Reuse the shared PiP window if one is already open.
    if (_sharedPip.window && !_sharedPip.window.closed && _sharedPip.container) {
      pipWindowRef.current = _sharedPip.window
      setPipContainer(_sharedPip.container)
      setPipActive(true)
      return
    }
    if (pipSupported === 'document') {
      try {
        const pipWindow = await documentPictureInPicture.requestWindow({
          width: 480,
          height: 320,
        })
        pipWindowRef.current = pipWindow

        // Copy stylesheets from the main document to the PiP window
        // so the moved elements look the same.
        const styleSheets = document.styleSheets
        for (const sheet of styleSheets) {
          try {
            const css = Array.from(sheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
            const styleEl = pipWindow.document.createElement('style')
            styleEl.textContent = css
            pipWindow.document.head.appendChild(styleEl)
          } catch {
            // Cross-origin stylesheets can't be read — skip.
            if (sheet.href) {
              const linkEl = pipWindow.document.createElement('link')
              linkEl.rel = 'stylesheet'
              linkEl.type = 'text/css'
              linkEl.href = sheet.href
              pipWindow.document.head.appendChild(linkEl)
            }
          }
        }

        // Set the PiP window body background to match the app theme.
        pipWindow.document.body.style.cssText = `
          margin: 0;
          background: #0d1117;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100vh;
        `

        // Create a container div inside the PiP window where React
        // will render content via portal.
        const container = pipWindow.document.createElement('div')
        container.style.cssText = 'display:flex;flex-direction:column;flex:1;min-height:0;'
        pipWindow.document.body.appendChild(container)

        // Store in singleton so the next CallView instance can reuse it.
        _sharedPip.window = pipWindow
        _sharedPip.container = container

        // When the PiP window is closed by the user, clean up singleton.
        // The event notifies other mounted CallView instances — this
        // instance's setState may be a no-op if it already unmounted.
        pipWindow.addEventListener('pagehide', () => {
          _sharedPip.window = null
          _sharedPip.container = null
          pipWindowRef.current = null
          setPipContainer(null)
          setPipActive(false)
          window.dispatchEvent(new Event('atlas-pip-closed'))
        })

        setPipContainer(container)
        setPipActive(true)
      } catch (err: unknown) {
        // Silently ignore NotAllowedError — happens when there's no
        // recent user activation (e.g. tab switch without a click).
        if ((err as Error).name !== 'NotAllowedError') {
          console.error('PiP: failed to open document PiP:', err)
        }
      }
    } else if (pipSupported === 'classic') {
      await openClassicPiP()
    }
  }, [pipSupported, openClassicPiP])

  const closePiP = useCallback(() => {
    if (pipWindowRef.current) {
      try { pipWindowRef.current.close() } catch {}
      pipWindowRef.current = null
    } else if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(() => {})
    }
    _sharedPip.window = null
    _sharedPip.container = null
    setPipContainer(null)
    setPipActive(false)
    window.dispatchEvent(new Event('atlas-pip-closed'))
  }, [])

  // Close PiP when the call ends — clears the singleton too.
  useEffect(() => {
    if (!isActive && pipActive) {
      closePiP()
    }
  }, [isActive, pipActive, closePiP])

  return {
    pipActive,
    pipSupported,
    openPiP,
    closePiP,
    pipContainer,
  }
}
